import { ColumnDef, Row, TableOptions } from '@tanstack/react-table';
import { ReactNode } from 'react';

export interface CustomDataTableProps<TData, TColumns extends ColumnDef<TData, unknown>[]>
  extends Partial<TableOptions<TData>> {
  columns: TColumns;
  data: TData[];
  empty?: React.ReactNode;
  enablePagination?: boolean;
  disableMultiExpansion?: boolean;
  children: (values: RowValues<TData, TColumns>, row: Row<TData>) => ReactNode;
}

type RowValues<
  TData,
  TColumns extends ColumnDef<TData, unknown>[],
> = TColumns extends (infer TColumnUnion)[]
  ? {
      [TColumn in TColumnUnion as TColumn extends { id: string }
        ? TColumn['id']
        : never]: TColumn extends {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- unknown causes bivariance error
        accessorFn: (data: any) => infer TValue;
      }
        ? TValue
        : TColumn extends { accessorKey: keyof TData }
          ? TData[TColumn['accessorKey']]
          : never;
    }
  : never;

export const getRowValues = <TData, TColumns extends ColumnDef<TData, unknown>[]>(
  row: Row<TData>
): RowValues<TData, TColumns> => {
  const cells = row._getAllCellsByColumnId();
  return Object.fromEntries(
    Object.entries(cells).map(([id, cell]) => [id, cell.getValue()])
  ) as RowValues<TData, TColumns>;
};
